// frontend/src/hooks/useSiteConfig.ts

import { useEffect } from 'react';
import { applyThemeToDocument, SiteTheme } from '../utils/theme';
import { COLLEGE_CONFIGS } from './collegeConfigs';

interface SiteConfig {
  name: string;
  description: string;
  keywords: string;
  sport: string | null;
  level: string | null;
  theme: SiteTheme;
  logoUrl?: string;
  faviconUrl?: string;
  appleTouchIconUrl?: string;
}

const SITE_CONFIGS: Record<string, SiteConfig> = {
  ...COLLEGE_CONFIGS,
  'the-sports-machine.com': {
    name: "The Sports Machine",
    faviconUrl: "/favicons/the-sports-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-sports-machine/apple-touch-icon.png",
    description: "Your central hub for advanced sports analytics and insights across multiple sports",
    keywords: "sports analytics, sports insights, comprehensive analytics",
    sport: null,
    level: null,
    logoUrl: "/logos/the-sports-machine.svg",
    theme: {
      primary: {
        '50': '#e8edec',
        '100': '#bccac9',
        '200': '#90a7a5',
        '300': '#638381',
        '400': '#426965',
        '500': '#214f4b', // TSM Dark Teal
        '600': '#1c433f',
        '700': '#173734',
        '800': '#122b29',
        '900': '#0d1f1e',
        '950': '#081312',
      },
      secondary: {
        '50': '#f5fdfe',
        '100': '#e1fafe',
        '200': '#cdf7fe',
        '300': '#b9f4fd',
        '400': '#aaf2fd',
        '500': '#9bf0fd', // TSM Light Cyan
        '600': '#83ccd7',
        '700': '#6ca8b1',
        '800': '#55848b',
        '900': '#3e6065',
        '950': '#263c3f',
      },
      accent: {
        '50': '#e7f8f0',
        '100': '#b9ecd4',
        '200': '#8ae0b8',
        '300': '#5bd39c',
        '400': '#38ca87',
        '500': '#16c172', // TSM Green
        '600': '#12a460',
        '700': '#0f874f',
        '800': '#0c6a3e',
        '900': '#084d2d',
        '950': '#05301c',
      },
      quaternary: {
        '50': '#e9fef4',
        '100': '#bffee0',
        '200': '#94fdcb',
        '300': '#69fcb6',
        '400': '#49fca7',
        '500': '#2afc98', // TSM Bright Green
        '600': '#23d681',
        '700': '#1db06a',
        '800': '#178a53',
        '900': '#10643c',
        '950': '#0a3f26',
      },
      quinary: {
        '50': '#e6fcee',
        '100': '#b5f8ce',
        '200': '#84f3ae',
        '300': '#52ee8e',
        '400': '#2deb76',
        '500': '#09e85e', // TSM Neon Green
        '600': '#07c54f',
        '700': '#06a241',
        '800': '#047f33',
        '900': '#035c25',
        '950': '#023a17',
      },
    }
  },
  'www.the-sports-machine.com': {
    name: "The Sports Machine",
    faviconUrl: "/favicons/the-sports-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-sports-machine/apple-touch-icon.png",
    description: "Your central hub for advanced sports analytics and insights across multiple sports",
    keywords: "sports analytics, sports insights, comprehensive analytics",
    sport: null,
    level: null,
    logoUrl: "/logos/the-sports-machine.svg",
    theme: {
      primary: {
        '50': '#e8edec',
        '100': '#bccac9',
        '200': '#90a7a5',
        '300': '#638381',
        '400': '#426965',
        '500': '#214f4b', // TSM Dark Teal
        '600': '#1c433f',
        '700': '#173734',
        '800': '#122b29',
        '900': '#0d1f1e',
        '950': '#081312',
      },
      secondary: {
        '50': '#f5fdfe',
        '100': '#e1fafe',
        '200': '#cdf7fe',
        '300': '#b9f4fd',
        '400': '#aaf2fd',
        '500': '#9bf0fd', // TSM Light Cyan
        '600': '#83ccd7',
        '700': '#6ca8b1',
        '800': '#55848b',
        '900': '#3e6065',
        '950': '#263c3f',
      },
      accent: {
        '50': '#e7f8f0',
        '100': '#b9ecd4',
        '200': '#8ae0b8',
        '300': '#5bd39c',
        '400': '#38ca87',
        '500': '#16c172', // TSM Green
        '600': '#12a460',
        '700': '#0f874f',
        '800': '#0c6a3e',
        '900': '#084d2d',
        '950': '#05301c',
      },
      quaternary: {
        '50': '#e9fef4',
        '100': '#bffee0',
        '200': '#94fdcb',
        '300': '#69fcb6',
        '400': '#49fca7',
        '500': '#2afc98', // TSM Bright Green
        '600': '#23d681',
        '700': '#1db06a',
        '800': '#178a53',
        '900': '#10643c',
        '950': '#0a3f26',
      },
      quinary: {
        '50': '#e6fcee',
        '100': '#b5f8ce',
        '200': '#84f3ae',
        '300': '#52ee8e',
        '400': '#2deb76',
        '500': '#09e85e', // TSM Neon Green
        '600': '#07c54f',
        '700': '#06a241',
        '800': '#047f33',
        '900': '#035c25',
        '950': '#023a17',
      },
    }
  },
  'the-football-machine.com': {
    name: "The Football Machine",
    faviconUrl: "/favicons/the-football-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-football-machine/apple-touch-icon.png",
    description: "Complete NFL championship history, Super Bowl winners, scores, and statistics",
    keywords: "NFL championships, Super Bowl history, NFL champions, football analytics",
    sport: 'football',
    level: 'professional',
    logoUrl: "/logos/the-football-machine.svg",
    theme: {
      primary: {
        '50': '#fce7ea',
        '100': '#f6b9c3',
        '200': '#f18b9b',
        '300': '#eb5d73',
        '400': '#e73a54',
        '500': '#e31837', // NFL Red
        '600': '#c0142e',
        '700': '#9e1026',
        '800': '#7c0d1e',
        '900': '#5a0916',
        '950': '#38060d',
      },
      secondary: {
        '50': '#fff7e8',
        '100': '#ffe9ba',
        '200': '#ffdb8d',
        '300': '#ffcd60',
        '400': '#ffc23e',
        '500': '#ffb81c', // NFL Gold
        '600': '#d89c17',
        '700': '#b28013',
        '800': '#8c650f',
        '900': '#66490b',
        '950': '#3f2e07',
      },
      accent: {
        '50': '#ffffff',
        '100': '#fafafa',
        '200': '#f5f5f5',
        '300': '#e0e0e0',
        '400': '#bdbdbd',
        '500': '#9e9e9e', // White (as gray scale)
        '600': '#757575',
        '700': '#616161',
        '800': '#424242',
        '900': '#212121',
        '950': '#0a0a0a',
      },
      quaternary: {
        '50': '#fbfbfb',
        '100': '#f5f5f5',
        '200': '#efefef',
        '300': '#e9e9e9',
        '400': '#e4e4e4',
        '500': '#e0e0e0', // Light Gray
        '600': '#bebebe',
        '700': '#9c9c9c',
        '800': '#7b7b7b',
        '900': '#595959',
        '950': '#383838',
      },
      quinary: {
        '50': '#e0e0e0',
        '100': '#bdbdbd',
        '200': '#9e9e9e',
        '300': '#757575',
        '400': '#616161',
        '500': '#424242', // Black (as gray scale)
        '600': '#303030',
        '700': '#212121',
        '800': '#121212',
        '900': '#0a0a0a',
        '950': '#000000',
      },
    }
  },
  'www.the-football-machine.com': {
    name: "The Football Machine",
    faviconUrl: "/favicons/the-football-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-football-machine/apple-touch-icon.png",
    description: "Complete NFL championship history, Super Bowl winners, scores, and statistics",
    keywords: "NFL championships, Super Bowl history, NFL champions, football analytics",
    sport: 'football',
    level: 'professional',
    logoUrl: "/logos/the-football-machine.svg",
    theme: {
      primary: {
        '50': '#fce7ea',
        '100': '#f6b9c3',
        '200': '#f18b9b',
        '300': '#eb5d73',
        '400': '#e73a54',
        '500': '#e31837', // NFL Red
        '600': '#c0142e',
        '700': '#9e1026',
        '800': '#7c0d1e',
        '900': '#5a0916',
        '950': '#38060d',
      },
      secondary: {
        '50': '#fff7e8',
        '100': '#ffe9ba',
        '200': '#ffdb8d',
        '300': '#ffcd60',
        '400': '#ffc23e',
        '500': '#ffb81c', // NFL Gold
        '600': '#d89c17',
        '700': '#b28013',
        '800': '#8c650f',
        '900': '#66490b',
        '950': '#3f2e07',
      },
      accent: {
        '50': '#ffffff',
        '100': '#fafafa',
        '200': '#f5f5f5',
        '300': '#e0e0e0',
        '400': '#bdbdbd',
        '500': '#9e9e9e', // White (as gray scale)
        '600': '#757575',
        '700': '#616161',
        '800': '#424242',
        '900': '#212121',
        '950': '#0a0a0a',
      },
      quaternary: {
        '50': '#fbfbfb',
        '100': '#f5f5f5',
        '200': '#efefef',
        '300': '#e9e9e9',
        '400': '#e4e4e4',
        '500': '#e0e0e0', // Light Gray
        '600': '#bebebe',
        '700': '#9c9c9c',
        '800': '#7b7b7b',
        '900': '#595959',
        '950': '#383838',
      },
      quinary: {
        '50': '#e0e0e0',
        '100': '#bdbdbd',
        '200': '#9e9e9e',
        '300': '#757575',
        '400': '#616161',
        '500': '#424242', // Black (as gray scale)
        '600': '#303030',
        '700': '#212121',
        '800': '#121212',
        '900': '#0a0a0a',
        '950': '#000000',
      },
    }
  },
  // Local development configurations
  'localhost:5184': { // Main site
    name: "The Sports Machine",
    faviconUrl: "/favicons/the-sports-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-sports-machine/apple-touch-icon.png",
    description: "Your central hub for advanced sports analytics and insights across multiple sports",
    keywords: "sports analytics, sports insights, comprehensive analytics", 
    sport: null,
    level: null,
    logoUrl: "/logos/the-sports-machine.svg",
    theme: {
      primary: {
        '50': '#e8edec',
        '100': '#bccac9',
        '200': '#90a7a5',
        '300': '#638381',
        '400': '#426965',
        '500': '#214f4b', // TSM Dark Teal
        '600': '#1c433f',
        '700': '#173734',
        '800': '#122b29',
        '900': '#0d1f1e',
        '950': '#081312',
      },
      secondary: {
        '50': '#f5fdfe',
        '100': '#e1fafe',
        '200': '#cdf7fe',
        '300': '#b9f4fd',
        '400': '#aaf2fd',
        '500': '#9bf0fd', // TSM Light Cyan
        '600': '#83ccd7',
        '700': '#6ca8b1',
        '800': '#55848b',
        '900': '#3e6065',
        '950': '#263c3f',
      },
      accent: {
        '50': '#e7f8f0',
        '100': '#b9ecd4',
        '200': '#8ae0b8',
        '300': '#5bd39c',
        '400': '#38ca87',
        '500': '#16c172', // TSM Green
        '600': '#12a460',
        '700': '#0f874f',
        '800': '#0c6a3e',
        '900': '#084d2d',
        '950': '#05301c',
      },
      quaternary: {
        '50': '#e9fef4',
        '100': '#bffee0',
        '200': '#94fdcb',
        '300': '#69fcb6',
        '400': '#49fca7',
        '500': '#2afc98', // TSM Bright Green
        '600': '#23d681',
        '700': '#1db06a',
        '800': '#178a53',
        '900': '#10643c',
        '950': '#0a3f26',
      },
      quinary: {
        '50': '#e6fcee',
        '100': '#b5f8ce',
        '200': '#84f3ae',
        '300': '#52ee8e',
        '400': '#2deb76',
        '500': '#09e85e', // TSM Neon Green
        '600': '#07c54f',
        '700': '#06a241',
        '800': '#047f33',
        '900': '#035c25',
        '950': '#023a17',
      },
    }
  },
  // NFL Machine local
  'nfl.localhost:5184': {
    name: "The Football Machine",
    faviconUrl: "/favicons/the-football-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-football-machine/apple-touch-icon.png",
    description: "Complete NFL championship history, Super Bowl winners, scores, and statistics",
    keywords: "NFL championships, Super Bowl history, NFL champions, football analytics",
    sport: 'football',
    level: 'professional',
    logoUrl: "/logos/the-football-machine.svg",
    theme: {
      primary: {
        '50': '#fce7ea',
        '100': '#f6b9c3',
        '200': '#f18b9b',
        '300': '#eb5d73',
        '400': '#e73a54',
        '500': '#e31837', // NFL Red
        '600': '#c0142e',
        '700': '#9e1026',
        '800': '#7c0d1e',
        '900': '#5a0916',
        '950': '#38060d',
      },
      secondary: {
        '50': '#fff7e8',
        '100': '#ffe9ba',
        '200': '#ffdb8d',
        '300': '#ffcd60',
        '400': '#ffc23e',
        '500': '#ffb81c', // NFL Gold
        '600': '#d89c17',
        '700': '#b28013',
        '800': '#8c650f',
        '900': '#66490b',
        '950': '#3f2e07',
      },
      accent: {
        '50': '#ffffff',
        '100': '#fafafa',
        '200': '#f5f5f5',
        '300': '#e0e0e0',
        '400': '#bdbdbd',
        '500': '#9e9e9e', // White (as gray scale)
        '600': '#757575',
        '700': '#616161',
        '800': '#424242',
        '900': '#212121',
        '950': '#0a0a0a',
      },
      quaternary: {
        '50': '#fbfbfb',
        '100': '#f5f5f5',
        '200': '#efefef',
        '300': '#e9e9e9',
        '400': '#e4e4e4',
        '500': '#e0e0e0', // Light Gray
        '600': '#bebebe',
        '700': '#9c9c9c',
        '800': '#7b7b7b',
        '900': '#595959',
        '950': '#383838',
      },
      quinary: {
        '50': '#e0e0e0',
        '100': '#bdbdbd',
        '200': '#9e9e9e',
        '300': '#757575',
        '400': '#616161',
        '500': '#424242', // Black (as gray scale)
        '600': '#303030',
        '700': '#212121',
        '800': '#121212',
        '900': '#0a0a0a',
        '950': '#000000',
      },
    }
  },
  // NBA Machine local
  'nba.localhost:5184': {
    name: "The Basketball Machine",
    faviconUrl: "/favicons/the-basketball-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-basketball-machine/apple-touch-icon.png",
    description: "Complete NBA championship history, Finals winners, scores, and statistics",
    keywords: "NBA championships, NBA Finals history, NBA champions, basketball analytics",
    sport: 'basketball',
    level: 'professional',
    logoUrl: "/logos/the-basketball-machine.svg",
    theme: {
      primary: {
        '50': '#ebe7e5',
        '100': '#c3b8b4',
        '200': '#9b8982',
        '300': '#735950',
        '400': '#55362a',
        '500': '#381305', // NBA Dark Brown
        '600': '#2f1004',
        '700': '#270d03',
        '800': '#1e0a02',
        '900': '#160702',
        '950': '#0e0401',
      },
      secondary: {
        '50': '#fdf8f6',
        '100': '#faeae4',
        '200': '#f8ddd3',
        '300': '#f5cfc1',
        '400': '#f3c5b4',
        '500': '#f1bba7', // NBA Light Peach
        '600': '#cc9e8d',
        '700': '#a88274',
        '800': '#84665b',
        '900': '#604a42',
        '950': '#3c2e29',
      },
      accent: {
        '50': '#f1eae8',
        '100': '#d5c1ba',
        '200': '#b9998d',
        '300': '#9d705f',
        '400': '#88513d',
        '500': '#74331b', // NBA Medium Brown
        '600': '#622b16',
        '700': '#512312',
        '800': '#3f1c0e',
        '900': '#2e140a',
        '950': '#1d0c06',
      },
      quaternary: {
        '50': '#fefaf9',
        '100': '#fcf2ee',
        '200': '#fbeae4',
        '300': '#fae2d9',
        '400': '#f9dcd1',
        '500': '#f8d6c9', // NBA Very Light Peach
        '600': '#d2b5aa',
        '700': '#ad958c',
        '800': '#88756e',
        '900': '#635550',
        '950': '#3e3532',
      },
      quinary: {
        '50': '#fef0eb',
        '100': '#fcd3c4',
        '200': '#fab69d',
        '300': '#f89976',
        '400': '#f78359',
        '500': '#f66e3c', // NBA Orange
        '600': '#d15d33',
        '700': '#ac4d2a',
        '800': '#873c21',
        '900': '#622c18',
        '950': '#3d1b0f',
      },
    }
  },
  // MLB Machine local
  'mlb.localhost:5184': {
    name: "The Baseball Machine",
    logoUrl: "/logos/the-baseball-machine.svg",
    faviconUrl: "/favicons/the-baseball-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-baseball-machine/apple-touch-icon.png",
    description: "Complete MLB championship history, World Series winners, scores, and statistics",
    keywords: "MLB championships, World Series history, MLB champions, baseball analytics",
    sport: 'baseball',
    level: 'professional',
    theme: {
      primary: {
        '50': '#e5ecf3',
        '100': '#b2c7db',
        '200': '#7fa2c3',
        '300': '#4c7dab',
        '400': '#266199',
        '500': '#004687', // MLB Blue
        '600': '#003b72',
        '700': '#00315e',
        '800': '#00264a',
        '900': '#001c36',
        '950': '#001121',
      },
      secondary: {
        '50': '#f1f7fb',
        '100': '#d7e7f4',
        '200': '#bcd8ee',
        '300': '#a1c9e7',
        '400': '#8dbde2',
        '500': '#7ab2dd', // MLB Light Blue
        '600': '#6797bb',
        '700': '#557c9a',
        '800': '#436179',
        '900': '#304758',
        '950': '#1e2c37',
      },
      accent: {
        '50': '#f8f5ee',
        '100': '#ece1cd',
        '200': '#dfcdad',
        '300': '#d2b98c',
        '400': '#c9aa73',
        '500': '#c09c5b', // MLB Gold
        '600': '#a3844d',
        '700': '#866d3f',
        '800': '#695532',
        '900': '#4c3e24',
        '950': '#302716',
      },
      quaternary: {
        '50': '#ffffff',
        '100': '#fafafa',
        '200': '#f5f5f5',
        '300': '#e0e0e0',
        '400': '#bdbdbd',
        '500': '#9e9e9e', // White (as gray scale)
        '600': '#757575',
        '700': '#616161',
        '800': '#424242',
        '900': '#212121',
        '950': '#0a0a0a',
      },
      quinary: {
        '50': '#e0e0e0',
        '100': '#bdbdbd',
        '200': '#9e9e9e',
        '300': '#757575',
        '400': '#616161',
        '500': '#424242', // Black (as gray scale)
        '600': '#303030',
        '700': '#212121',
        '800': '#121212',
        '900': '#0a0a0a',
        '950': '#000000',
      },
    }
  },
  // Basketball Machine production
  'the-basketball-machine.com': {
    name: "The Basketball Machine",
    faviconUrl: "/favicons/the-basketball-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-basketball-machine/apple-touch-icon.png",
    description: "Complete NBA championship history, Finals winners, scores, and statistics",
    keywords: "NBA championships, NBA Finals history, NBA champions, basketball analytics",
    sport: 'basketball',
    level: 'professional',
    logoUrl: "/logos/the-basketball-machine.svg",
    theme: {
      primary: {
        '50': '#ebe7e5',
        '100': '#c3b8b4',
        '200': '#9b8982',
        '300': '#735950',
        '400': '#55362a',
        '500': '#381305', // NBA Dark Brown
        '600': '#2f1004',
        '700': '#270d03',
        '800': '#1e0a02',
        '900': '#160702',
        '950': '#0e0401',
      },
      secondary: {
        '50': '#fdf8f6',
        '100': '#faeae4',
        '200': '#f8ddd3',
        '300': '#f5cfc1',
        '400': '#f3c5b4',
        '500': '#f1bba7', // NBA Light Peach
        '600': '#cc9e8d',
        '700': '#a88274',
        '800': '#84665b',
        '900': '#604a42',
        '950': '#3c2e29',
      },
      accent: {
        '50': '#f1eae8',
        '100': '#d5c1ba',
        '200': '#b9998d',
        '300': '#9d705f',
        '400': '#88513d',
        '500': '#74331b', // NBA Medium Brown
        '600': '#622b16',
        '700': '#512312',
        '800': '#3f1c0e',
        '900': '#2e140a',
        '950': '#1d0c06',
      },
      quaternary: {
        '50': '#fefaf9',
        '100': '#fcf2ee',
        '200': '#fbeae4',
        '300': '#fae2d9',
        '400': '#f9dcd1',
        '500': '#f8d6c9', // NBA Very Light Peach
        '600': '#d2b5aa',
        '700': '#ad958c',
        '800': '#88756e',
        '900': '#635550',
        '950': '#3e3532',
      },
      quinary: {
        '50': '#fef0eb',
        '100': '#fcd3c4',
        '200': '#fab69d',
        '300': '#f89976',
        '400': '#f78359',
        '500': '#f66e3c', // NBA Orange
        '600': '#d15d33',
        '700': '#ac4d2a',
        '800': '#873c21',
        '900': '#622c18',
        '950': '#3d1b0f',
      },
    }
  },
  'www.the-basketball-machine.com': {
    name: "The Basketball Machine",
    description: "Complete NBA championship history, Finals winners, scores, and statistics",
    keywords: "NBA championships, NBA Finals history, NBA champions, basketball analytics",
    sport: 'basketball',
    level: 'professional',
    logoUrl: "/logos/the-basketball-machine.svg",
    faviconUrl: "/favicons/the-basketball-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-basketball-machine/apple-touch-icon.png",
    theme: {
      primary: {
        '50': '#ebe7e5',
        '100': '#c3b8b4',
        '200': '#9b8982',
        '300': '#735950',
        '400': '#55362a',
        '500': '#381305', // NBA Dark Brown
        '600': '#2f1004',
        '700': '#270d03',
        '800': '#1e0a02',
        '900': '#160702',
        '950': '#0e0401',
      },
      secondary: {
        '50': '#fdf8f6',
        '100': '#faeae4',
        '200': '#f8ddd3',
        '300': '#f5cfc1',
        '400': '#f3c5b4',
        '500': '#f1bba7', // NBA Light Peach
        '600': '#cc9e8d',
        '700': '#a88274',
        '800': '#84665b',
        '900': '#604a42',
        '950': '#3c2e29',
      },
      accent: {
        '50': '#f1eae8',
        '100': '#d5c1ba',
        '200': '#b9998d',
        '300': '#9d705f',
        '400': '#88513d',
        '500': '#74331b', // NBA Medium Brown
        '600': '#622b16',
        '700': '#512312',
        '800': '#3f1c0e',
        '900': '#2e140a',
        '950': '#1d0c06',
      },
      quaternary: {
        '50': '#fefaf9',
        '100': '#fcf2ee',
        '200': '#fbeae4',
        '300': '#fae2d9',
        '400': '#f9dcd1',
        '500': '#f8d6c9', // NBA Very Light Peach
        '600': '#d2b5aa',
        '700': '#ad958c',
        '800': '#88756e',
        '900': '#635550',
        '950': '#3e3532',
      },
      quinary: {
        '50': '#fef0eb',
        '100': '#fcd3c4',
        '200': '#fab69d',
        '300': '#f89976',
        '400': '#f78359',
        '500': '#f66e3c', // NBA Orange
        '600': '#d15d33',
        '700': '#ac4d2a',
        '800': '#873c21',
        '900': '#622c18',
        '950': '#3d1b0f',
      },
    }
  },
  // Baseball Machine production
  'the-baseball-machine.com': {
    name: "The Baseball Machine",
    faviconUrl: "/favicons/the-baseball-machine/favicon.ico",
    logoUrl: "/logos/the-baseball-machine.svg",
    appleTouchIconUrl: "/favicons/the-baseball-machine/apple-touch-icon.png",
    description: "Complete MLB championship history, World Series winners, scores, and statistics",
    keywords: "MLB championships, World Series history, MLB champions, baseball analytics",
    sport: 'baseball',
    level: 'professional',
    theme: {
      primary: {
        '50': '#e5ecf3',
        '100': '#b2c7db',
        '200': '#7fa2c3',
        '300': '#4c7dab',
        '400': '#266199',
        '500': '#004687', // MLB Blue
        '600': '#003b72',
        '700': '#00315e',
        '800': '#00264a',
        '900': '#001c36',
        '950': '#001121',
      },
      secondary: {
        '50': '#f1f7fb',
        '100': '#d7e7f4',
        '200': '#bcd8ee',
        '300': '#a1c9e7',
        '400': '#8dbde2',
        '500': '#7ab2dd', // MLB Light Blue
        '600': '#6797bb',
        '700': '#557c9a',
        '800': '#436179',
        '900': '#304758',
        '950': '#1e2c37',
      },
      accent: {
        '50': '#f8f5ee',
        '100': '#ece1cd',
        '200': '#dfcdad',
        '300': '#d2b98c',
        '400': '#c9aa73',
        '500': '#c09c5b', // MLB Gold
        '600': '#a3844d',
        '700': '#866d3f',
        '800': '#695532',
        '900': '#4c3e24',
        '950': '#302716',
      },
      quaternary: {
        '50': '#ffffff',
        '100': '#fafafa',
        '200': '#f5f5f5',
        '300': '#e0e0e0',
        '400': '#bdbdbd',
        '500': '#9e9e9e', // White (as gray scale)
        '600': '#757575',
        '700': '#616161',
        '800': '#424242',
        '900': '#212121',
        '950': '#0a0a0a',
      },
      quinary: {
        '50': '#e0e0e0',
        '100': '#bdbdbd',
        '200': '#9e9e9e',
        '300': '#757575',
        '400': '#616161',
        '500': '#424242', // Black (as gray scale)
        '600': '#303030',
        '700': '#212121',
        '800': '#121212',
        '900': '#0a0a0a',
        '950': '#000000',
      },
    }
  },
  'www.the-baseball-machine.com': {
    name: "The Baseball Machine",
    logoUrl: "/logos/the-baseball-machine.svg",
    faviconUrl: "/favicons/the-baseball-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-baseball-machine/apple-touch-icon.png",
    description: "Complete MLB championship history, World Series winners, scores, and statistics",
    keywords: "MLB championships, World Series history, MLB champions, baseball analytics",
    sport: 'baseball',
    level: 'professional',
    theme: {
      primary: {
        '50': '#e5ecf3',
        '100': '#b2c7db',
        '200': '#7fa2c3',
        '300': '#4c7dab',
        '400': '#266199',
        '500': '#004687', // MLB Blue
        '600': '#003b72',
        '700': '#00315e',
        '800': '#00264a',
        '900': '#001c36',
        '950': '#001121',
      },
      secondary: {
        '50': '#f1f7fb',
        '100': '#d7e7f4',
        '200': '#bcd8ee',
        '300': '#a1c9e7',
        '400': '#8dbde2',
        '500': '#7ab2dd', // MLB Light Blue
        '600': '#6797bb',
        '700': '#557c9a',
        '800': '#436179',
        '900': '#304758',
        '950': '#1e2c37',
      },
      accent: {
        '50': '#f8f5ee',
        '100': '#ece1cd',
        '200': '#dfcdad',
        '300': '#d2b98c',
        '400': '#c9aa73',
        '500': '#c09c5b', // MLB Gold
        '600': '#a3844d',
        '700': '#866d3f',
        '800': '#695532',
        '900': '#4c3e24',
        '950': '#302716',
      },
      quaternary: {
        '50': '#ffffff',
        '100': '#fafafa',
        '200': '#f5f5f5',
        '300': '#e0e0e0',
        '400': '#bdbdbd',
        '500': '#9e9e9e', // White (as gray scale)
        '600': '#757575',
        '700': '#616161',
        '800': '#424242',
        '900': '#212121',
        '950': '#0a0a0a',
      },
      quinary: {
        '50': '#e0e0e0',
        '100': '#bdbdbd',
        '200': '#9e9e9e',
        '300': '#757575',
        '400': '#616161',
        '500': '#424242', // Black (as gray scale)
        '600': '#303030',
        '700': '#212121',
        '800': '#121212',
        '900': '#0a0a0a',
        '950': '#000000',
      },
    }
  },
  // Hockey Machine production
  'the-hockey-machine.com': {
    name: "The Hockey Machine",
    faviconUrl: "/favicons/the-hockey-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-hockey-machine/apple-touch-icon.png",
    description: "Complete NHL championship history, Stanley Cup winners, scores, and statistics",
    keywords: "NHL championships, Stanley Cup history, NHL champions, hockey analytics",
    sport: 'hockey',
    level: 'professional',
    logoUrl: "/logos/the-hockey-machine.svg",
    theme: {
      primary: {
        '50': '#e0e0e0',
        '100': '#bdbdbd',
        '200': '#9e9e9e',
        '300': '#757575',
        '400': '#616161',
        '500': '#424242', // NHL Near Black
        '600': '#303030',
        '700': '#212121',
        '800': '#121212',
        '900': '#0a0a0a',
        '950': '#000000',
      },
      secondary: {
        '50': '#ffffff',
        '100': '#fafafa',
        '200': '#f5f5f5',
        '300': '#e0e0e0',
        '400': '#bdbdbd',
        '500': '#9e9e9e', // NHL Near White (as gray scale)
        '600': '#757575',
        '700': '#616161',
        '800': '#424242',
        '900': '#212121',
        '950': '#0a0a0a',
      },
      accent: {
        '50': '#e9eaea',
        '100': '#c0c0c0',
        '200': '#969696',
        '300': '#6c6c6c',
        '400': '#4c4d4d',
        '500': '#2d2e2e', // NHL Dark Gray
        '600': '#262727',
        '700': '#1f2020',
        '800': '#181919',
        '900': '#121212',
        '950': '#0b0b0b',
      },
      quaternary: {
        '50': '#fcfbfb',
        '100': '#f6f4f5',
        '200': '#f1eeee',
        '300': '#ece7e7',
        '400': '#e8e2e2',
        '500': '#e4ddde', // NHL Light Gray
        '600': '#c1bbbc',
        '700': '#9f9a9b',
        '800': '#7d797a',
        '900': '#5b5858',
        '950': '#393737',
      },
      quinary: {
        '50': '#f0f0f0',
        '100': '#d4d2d2',
        '200': '#b8b4b4',
        '300': '#9b9696',
        '400': '#867f7f',
        '500': '#716969', // NHL Medium Gray
        '600': '#605959',
        '700': '#4f4949',
        '800': '#3e3939',
        '900': '#2d2a2a',
        '950': '#1c1a1a',
      },
    }
  },
  'www.the-hockey-machine.com': {
    name: "The Hockey Machine",
    faviconUrl: "/favicons/the-hockey-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-hockey-machine/apple-touch-icon.png",
    description: "Complete NHL championship history, Stanley Cup winners, scores, and statistics",
    keywords: "NHL championships, Stanley Cup history, NHL champions, hockey analytics",
    sport: 'hockey',
    level: 'professional',
    logoUrl: "/logos/the-hockey-machine.svg",
    theme: {
      primary: {
        '50': '#e0e0e0',
        '100': '#bdbdbd',
        '200': '#9e9e9e',
        '300': '#757575',
        '400': '#616161',
        '500': '#424242', // NHL Near Black
        '600': '#303030',
        '700': '#212121',
        '800': '#121212',
        '900': '#0a0a0a',
        '950': '#000000',
      },
      secondary: {
        '50': '#ffffff',
        '100': '#fafafa',
        '200': '#f5f5f5',
        '300': '#e0e0e0',
        '400': '#bdbdbd',
        '500': '#9e9e9e', // NHL Near White (as gray scale)
        '600': '#757575',
        '700': '#616161',
        '800': '#424242',
        '900': '#212121',
        '950': '#0a0a0a',
      },
      accent: {
        '50': '#e9eaea',
        '100': '#c0c0c0',
        '200': '#969696',
        '300': '#6c6c6c',
        '400': '#4c4d4d',
        '500': '#2d2e2e', // NHL Dark Gray
        '600': '#262727',
        '700': '#1f2020',
        '800': '#181919',
        '900': '#121212',
        '950': '#0b0b0b',
      },
      quaternary: {
        '50': '#fcfbfb',
        '100': '#f6f4f5',
        '200': '#f1eeee',
        '300': '#ece7e7',
        '400': '#e8e2e2',
        '500': '#e4ddde', // NHL Light Gray
        '600': '#c1bbbc',
        '700': '#9f9a9b',
        '800': '#7d797a',
        '900': '#5b5858',
        '950': '#393737',
      },
      quinary: {
        '50': '#f0f0f0',
        '100': '#d4d2d2',
        '200': '#b8b4b4',
        '300': '#9b9696',
        '400': '#867f7f',
        '500': '#716969', // NHL Medium Gray
        '600': '#605959',
        '700': '#4f4949',
        '800': '#3e3939',
        '900': '#2d2a2a',
        '950': '#1c1a1a',
      },
    }
  },
  // NHL Machine local
  'nhl.localhost:5184': {
    name: "The Hockey Machine",
    description: "Complete NHL championship history, Stanley Cup winners, scores, and statistics",
    keywords: "NHL championships, Stanley Cup history, NHL champions, hockey analytics",
    sport: 'hockey',
    level: 'professional',
    logoUrl: "/logos/the-hockey-machine.svg",
    faviconUrl: "/favicons/the-hockey-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-hockey-machine/apple-touch-icon.png",
    theme: {
      primary: {
        '50': '#e0e0e0',
        '100': '#bdbdbd',
        '200': '#9e9e9e',
        '300': '#757575',
        '400': '#616161',
        '500': '#424242', // NHL Near Black
        '600': '#303030',
        '700': '#212121',
        '800': '#121212',
        '900': '#0a0a0a',
        '950': '#000000',
      },
      secondary: {
        '50': '#ffffff',
        '100': '#fafafa',
        '200': '#f5f5f5',
        '300': '#e0e0e0',
        '400': '#bdbdbd',
        '500': '#9e9e9e', // NHL Near White (as gray scale)
        '600': '#757575',
        '700': '#616161',
        '800': '#424242',
        '900': '#212121',
        '950': '#0a0a0a',
      },
      accent: {
        '50': '#e9eaea',
        '100': '#c0c0c0',
        '200': '#969696',
        '300': '#6c6c6c',
        '400': '#4c4d4d',
        '500': '#2d2e2e', // NHL Dark Gray
        '600': '#262727',
        '700': '#1f2020',
        '800': '#181919',
        '900': '#121212',
        '950': '#0b0b0b',
      },
      quaternary: {
        '50': '#fcfbfb',
        '100': '#f6f4f5',
        '200': '#f1eeee',
        '300': '#ece7e7',
        '400': '#e8e2e2',
        '500': '#e4ddde', // NHL Light Gray
        '600': '#c1bbbc',
        '700': '#9f9a9b',
        '800': '#7d797a',
        '900': '#5b5858',
        '950': '#393737',
      },
      quinary: {
        '50': '#f0f0f0',
        '100': '#d4d2d2',
        '200': '#b8b4b4',
        '300': '#9b9696',
        '400': '#867f7f',
        '500': '#716969', // NHL Medium Gray
        '600': '#605959',
        '700': '#4f4949',
        '800': '#3e3939',
        '900': '#2d2a2a',
        '950': '#1c1a1a',
      },
    }
  },
  // College Football Machine production
  'the-college-football-machine.com': {
    name: "The College Football Machine",
    description: "Complete college football championship history, bowl games, and statistics",
    keywords: "college football championships, CFP history, bowl games, NCAA football",
    sport: 'football',
    level: 'college',
    logoUrl: "/logos/the-college-football-machine.svg",
    faviconUrl: "/favicons/the-college-football-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-college-football-machine/apple-touch-icon.png",
    theme: {
      primary: {
        '50': '#f3e7e9',
        '100': '#dcb9bd',
        '200': '#c58b92',
        '300': '#ad5d67',
        '400': '#9c3a46',
        '500': '#8b1826', // CFB Maroon
        '600': '#761420',
        '700': '#61101a',
        '800': '#4c0d14',
        '900': '#37090f',
        '950': '#220609',
      },
      secondary: {
        '50': '#f7fcfb',
        '100': '#e8f6f5',
        '200': '#d9f1ef',
        '300': '#caece9',
        '400': '#bfe8e4',
        '500': '#b4e4e0', // CFB Light Teal
        '600': '#99c1be',
        '700': '#7d9f9c',
        '800': '#637d7b',
        '900': '#485b59',
        '950': '#2d3938',
      },
      accent: {
        '50': '#f5eaeb',
        '100': '#e1c2c6',
        '200': '#ce99a0',
        '300': '#bb707a',
        '400': '#ac525d',
        '500': '#9e3441', // CFB Light Maroon
        '600': '#862c37',
        '700': '#6e242d',
        '800': '#561c23',
        '900': '#3f141a',
        '950': '#270d10',
      },
      quaternary: {
        '50': '#f8fbfb',
        '100': '#eaf4f3',
        '200': '#ddeeec',
        '300': '#cfe7e5',
        '400': '#c5e2df',
        '500': '#bbddda', // CFB Very Light Teal
        '600': '#9ebbb9',
        '700': '#829a98',
        '800': '#667977',
        '900': '#4a5857',
        '950': '#2e3736',
      },
      quinary: {
        '50': '#fbeaec',
        '100': '#f4c1c6',
        '200': '#ed98a0',
        '300': '#e66f7a',
        '400': '#e2515e',
        '500': '#de2f43', // CFB Bright Red
        '600': '#bd2838',
        '700': '#9d212e',
        '800': '#7c1a24',
        '900': '#5b131a',
        '950': '#3a0c10',
      },
    }
  },
  'www.the-college-football-machine.com': {
    name: "The College Football Machine",
    description: "Complete college football championship history, bowl games, and statistics",
    keywords: "college football championships, CFP history, bowl games, NCAA football",
    sport: 'football',
    level: 'college',
    logoUrl: "/logos/the-college-football-machine.svg",
    faviconUrl: "/favicons/the-college-football-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-college-football-machine/apple-touch-icon.png",
    theme: {
      primary: {
        '50': '#f3e7e9',
        '100': '#dcb9bd',
        '200': '#c58b92',
        '300': '#ad5d67',
        '400': '#9c3a46',
        '500': '#8b1826', // CFB Maroon
        '600': '#761420',
        '700': '#61101a',
        '800': '#4c0d14',
        '900': '#37090f',
        '950': '#220609',
      },
      secondary: {
        '50': '#f7fcfb',
        '100': '#e8f6f5',
        '200': '#d9f1ef',
        '300': '#caece9',
        '400': '#bfe8e4',
        '500': '#b4e4e0', // CFB Light Teal
        '600': '#99c1be',
        '700': '#7d9f9c',
        '800': '#637d7b',
        '900': '#485b59',
        '950': '#2d3938',
      },
      accent: {
        '50': '#f5eaeb',
        '100': '#e1c2c6',
        '200': '#ce99a0',
        '300': '#bb707a',
        '400': '#ac525d',
        '500': '#9e3441', // CFB Light Maroon
        '600': '#862c37',
        '700': '#6e242d',
        '800': '#561c23',
        '900': '#3f141a',
        '950': '#270d10',
      },
      quaternary: {
        '50': '#f8fbfb',
        '100': '#eaf4f3',
        '200': '#ddeeec',
        '300': '#cfe7e5',
        '400': '#c5e2df',
        '500': '#bbddda', // CFB Very Light Teal
        '600': '#9ebbb9',
        '700': '#829a98',
        '800': '#667977',
        '900': '#4a5857',
        '950': '#2e3736',
      },
      quinary: {
        '50': '#fbeaec',
        '100': '#f4c1c6',
        '200': '#ed98a0',
        '300': '#e66f7a',
        '400': '#e2515e',
        '500': '#de2f43', // CFB Bright Red
        '600': '#bd2838',
        '700': '#9d212e',
        '800': '#7c1a24',
        '900': '#5b131a',
        '950': '#3a0c10',
      },
    }
  },
  // College Football Machine local
  'cfb.localhost:5184': {
    name: "The College Football Machine",
    description: "Complete college football championship history, bowl games, and statistics",
    keywords: "college football championships, CFP history, bowl games, NCAA football",
    sport: 'football',
    level: 'college',
    logoUrl: "/logos/the-college-football-machine.svg",
    faviconUrl: "/favicons/the-college-football-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-college-football-machine/apple-touch-icon.png",
    theme: {
      primary: {
        '50': '#f3e7e9',
        '100': '#dcb9bd',
        '200': '#c58b92',
        '300': '#ad5d67',
        '400': '#9c3a46',
        '500': '#8b1826', // CFB Maroon
        '600': '#761420',
        '700': '#61101a',
        '800': '#4c0d14',
        '900': '#37090f',
        '950': '#220609',
      },
      secondary: {
        '50': '#f7fcfb',
        '100': '#e8f6f5',
        '200': '#d9f1ef',
        '300': '#caece9',
        '400': '#bfe8e4',
        '500': '#b4e4e0', // CFB Light Teal
        '600': '#99c1be',
        '700': '#7d9f9c',
        '800': '#637d7b',
        '900': '#485b59',
        '950': '#2d3938',
      },
      accent: {
        '50': '#f5eaeb',
        '100': '#e1c2c6',
        '200': '#ce99a0',
        '300': '#bb707a',
        '400': '#ac525d',
        '500': '#9e3441', // CFB Light Maroon
        '600': '#862c37',
        '700': '#6e242d',
        '800': '#561c23',
        '900': '#3f141a',
        '950': '#270d10',
      },
      quaternary: {
        '50': '#f8fbfb',
        '100': '#eaf4f3',
        '200': '#ddeeec',
        '300': '#cfe7e5',
        '400': '#c5e2df',
        '500': '#bbddda', // CFB Very Light Teal
        '600': '#9ebbb9',
        '700': '#829a98',
        '800': '#667977',
        '900': '#4a5857',
        '950': '#2e3736',
      },
      quinary: {
        '50': '#fbeaec',
        '100': '#f4c1c6',
        '200': '#ed98a0',
        '300': '#e66f7a',
        '400': '#e2515e',
        '500': '#de2f43', // CFB Bright Red
        '600': '#bd2838',
        '700': '#9d212e',
        '800': '#7c1a24',
        '900': '#5b131a',
        '950': '#3a0c10',
      },
    }
  },
  // College Basketball Machine production
  'the-college-basketball-machine.com': {
    name: "The College Basketball Machine",
    description: "Complete college basketball championship history, Final Four, and statistics",
    keywords: "college basketball championships, NCAA Final Four, basketball analytics",
    sport: 'basketball',
    level: 'college',
    logoUrl: "/logos/the-college-basketball-machine.svg",
    faviconUrl: "/favicons/the-college-basketball-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-college-basketball-machine/apple-touch-icon.png",
    theme: {
      primary: {
        '50': '#e5e6ea',
        '100': '#b2b6c0',
        '200': '#7f8697',
        '300': '#4c566d',
        '400': '#26324e',
        '500': '#000e2f', // CBB Dark Navy
        '600': '#000b27',
        '700': '#000920',
        '800': '#000719',
        '900': '#000512',
        '950': '#00030b',
      },
      secondary: {
        '50': '#f9fbfe',
        '100': '#eff4fc',
        '200': '#e4eefb',
        '300': '#d9e7f9',
        '400': '#d1e2f8',
        '500': '#caddf7', // CBB Light Blue
        '600': '#abbbd1',
        '700': '#8d9aac',
        '800': '#6f7987',
        '900': '#505862',
        '950': '#32373d',
      },
      accent: {
        '50': '#e5edf8',
        '100': '#b2caea',
        '200': '#7fa8dc',
        '300': '#4c85ce',
        '400': '#266bc4',
        '500': '#0051ba', // CBB Blue
        '600': '#00449e',
        '700': '#003882',
        '800': '#002c66',
        '900': '#00204a',
        '950': '#00142e',
      },
      quaternary: {
        '50': '#f1f7fa',
        '100': '#d7e7f2',
        '200': '#bdd7e9',
        '300': '#a2c7e0',
        '400': '#8ebbda',
        '500': '#7bafd4', // CBB Medium Blue
        '600': '#6894b4',
        '700': '#567a94',
        '800': '#436074',
        '900': '#314654',
        '950': '#1e2b35',
      },
      quinary: {
        '50': '#e5eaf3',
        '100': '#b2c2d8',
        '200': '#7f9abd',
        '300': '#4c72a2',
        '400': '#265591',
        '500': '#003087', // CBB Navy
        '600': '#002a72',
        '700': '#00245e',
        '800': '#001e4a',
        '900': '#001836',
        '950': '#001221',
      },
    }
  },
  'www.the-college-basketball-machine.com': {
    name: "The College Basketball Machine",
    description: "Complete college basketball championship history, Final Four, and statistics",
    keywords: "college basketball championships, NCAA Final Four, basketball analytics",
    sport: 'basketball',
    level: 'college',
    logoUrl: "/logos/the-college-basketball-machine.svg",
    faviconUrl: "/favicons/the-college-basketball-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-college-basketball-machine/apple-touch-icon.png",
    theme: {
      primary: {
        '50': '#e5e6ea',
        '100': '#b2b6c0',
        '200': '#7f8697',
        '300': '#4c566d',
        '400': '#26324e',
        '500': '#000e2f', // CBB Dark Navy
        '600': '#000b27',
        '700': '#000920',
        '800': '#000719',
        '900': '#000512',
        '950': '#00030b',
      },
      secondary: {
        '50': '#f9fbfe',
        '100': '#eff4fc',
        '200': '#e4eefb',
        '300': '#d9e7f9',
        '400': '#d1e2f8',
        '500': '#caddf7', // CBB Light Blue
        '600': '#abbbd1',
        '700': '#8d9aac',
        '800': '#6f7987',
        '900': '#505862',
        '950': '#32373d',
      },
      accent: {
        '50': '#e5edf8',
        '100': '#b2caea',
        '200': '#7fa8dc',
        '300': '#4c85ce',
        '400': '#266bc4',
        '500': '#0051ba', // CBB Blue
        '600': '#00449e',
        '700': '#003882',
        '800': '#002c66',
        '900': '#00204a',
        '950': '#00142e',
      },
      quaternary: {
        '50': '#f1f7fa',
        '100': '#d7e7f2',
        '200': '#bdd7e9',
        '300': '#a2c7e0',
        '400': '#8ebbda',
        '500': '#7bafd4', // CBB Medium Blue
        '600': '#6894b4',
        '700': '#567a94',
        '800': '#436074',
        '900': '#314654',
        '950': '#1e2b35',
      },
      quinary: {
        '50': '#e5eaf3',
        '100': '#b2c2d8',
        '200': '#7f9abd',
        '300': '#4c72a2',
        '400': '#265591',
        '500': '#003087', // CBB Navy
        '600': '#002a72',
        '700': '#00245e',
        '800': '#001e4a',
        '900': '#001836',
        '950': '#001221',
      },
    }
  },
  // College Basketball Machine local
  'cbb.localhost:5184': {
    name: "The College Basketball Machine",
    description: "Complete college basketball championship history, Final Four, and statistics",
    keywords: "college basketball championships, NCAA Final Four, basketball analytics",
    sport: 'basketball',
    level: 'college',
    logoUrl: "/logos/the-college-basketball-machine.svg",
    faviconUrl: "/favicons/the-college-basketball-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-college-basketball-machine/apple-touch-icon.png",
    theme: {
      primary: {
        '50': '#e5e6ea',
        '100': '#b2b6c0',
        '200': '#7f8697',
        '300': '#4c566d',
        '400': '#26324e',
        '500': '#000e2f', // CBB Dark Navy
        '600': '#000b27',
        '700': '#000920',
        '800': '#000719',
        '900': '#000512',
        '950': '#00030b',
      },
      secondary: {
        '50': '#f9fbfe',
        '100': '#eff4fc',
        '200': '#e4eefb',
        '300': '#d9e7f9',
        '400': '#d1e2f8',
        '500': '#caddf7', // CBB Light Blue
        '600': '#abbbd1',
        '700': '#8d9aac',
        '800': '#6f7987',
        '900': '#505862',
        '950': '#32373d',
      },
      accent: {
        '50': '#e5edf8',
        '100': '#b2caea',
        '200': '#7fa8dc',
        '300': '#4c85ce',
        '400': '#266bc4',
        '500': '#0051ba', // CBB Blue
        '600': '#00449e',
        '700': '#003882',
        '800': '#002c66',
        '900': '#00204a',
        '950': '#00142e',
      },
      quaternary: {
        '50': '#f1f7fa',
        '100': '#d7e7f2',
        '200': '#bdd7e9',
        '300': '#a2c7e0',
        '400': '#8ebbda',
        '500': '#7bafd4', // CBB Medium Blue
        '600': '#6894b4',
        '700': '#567a94',
        '800': '#436074',
        '900': '#314654',
        '950': '#1e2b35',
      },
      quinary: {
        '50': '#e5eaf3',
        '100': '#b2c2d8',
        '200': '#7f9abd',
        '300': '#4c72a2',
        '400': '#265591',
        '500': '#003087', // CBB Navy
        '600': '#002a72',
        '700': '#00245e',
        '800': '#001e4a',
        '900': '#001836',
        '950': '#001221',
      },
    }
  },
  // College Baseball Machine production
  'the-college-baseball-machine.com': {
    name: "The College Baseball Machine",
    description: "Complete college baseball championship history, College World Series, and statistics",
    keywords: "college baseball championships, NCAA College World Series, baseball analytics",
    sport: 'baseball',
    level: 'college',
    logoUrl: "/logos/the-college-baseball-machine.svg",
    faviconUrl: "/favicons/the-college-baseball-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-college-baseball-machine/apple-touch-icon.png",
    theme: {
      primary: {
        '50': '#e8e9e7',
        '100': '#babdba',
        '200': '#8d918c',
        '300': '#60655e',
        '400': '#3e443b',
        '500': '#1c2419',
        '600': '#171e15',
        '700': '#131911',
        '800': '#0f130d',
        '900': '#0b0e0a',
        '950': '#070906',
      },
      secondary: {
        '50': '#fbfcfb',
        '100': '#f5f7f4',
        '200': '#eef2ed',
        '300': '#e7ede5',
        '400': '#e2e9e0',
        '500': '#dee6db',
        '600': '#bcc3ba',
        '700': '#9ba199',
        '800': '#7a7e78',
        '900': '#585c57',
        '950': '#373936',
      },
      accent: {
        '50': '#eef0e8',
        '100': '#ced4bb',
        '200': '#adb88e',
        '300': '#8c9b61',
        '400': '#74863f',
        '500': '#5c711e',
        '600': '#4e6019',
        '700': '#404f15',
        '800': '#323e10',
        '900': '#242d0c',
        '950': '#171c07',
      },
      quaternary: {
        '50': '#fbfdfa',
        '100': '#f3fbf0',
        '200': '#ebf9e6',
        '300': '#e3f7dc',
        '400': '#ddf5d4',
        '500': '#d8f4cd',
        '600': '#b7cfae',
        '700': '#97aa8f',
        '800': '#768670',
        '900': '#566152',
        '950': '#363d33',
      },
      quinary: {
        '50': '#e5f7e7',
        '100': '#b2e9b7',
        '200': '#7fdb88',
        '300': '#4ccd59',
        '400': '#26c235',
        '500': '#00b812',
        '600': '#009c0f',
        '700': '#00800c',
        '800': '#006509',
        '900': '#004907',
        '950': '#002e04',
      },
    }
  },
  'www.the-college-baseball-machine.com': {
    name: "The College Baseball Machine",
    description: "Complete college baseball championship history, College World Series, and statistics",
    keywords: "college baseball championships, NCAA College World Series, baseball analytics",
    sport: 'baseball',
    level: 'college',
    logoUrl: "/logos/the-college-baseball-machine.svg",
    faviconUrl: "/favicons/the-college-baseball-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-college-baseball-machine/apple-touch-icon.png",
    theme: {
      primary: {
        '50': '#e8e9e7',
        '100': '#babdba',
        '200': '#8d918c',
        '300': '#60655e',
        '400': '#3e443b',
        '500': '#1c2419',
        '600': '#171e15',
        '700': '#131911',
        '800': '#0f130d',
        '900': '#0b0e0a',
        '950': '#070906',
      },
      secondary: {
        '50': '#fbfcfb',
        '100': '#f5f7f4',
        '200': '#eef2ed',
        '300': '#e7ede5',
        '400': '#e2e9e0',
        '500': '#dee6db',
        '600': '#bcc3ba',
        '700': '#9ba199',
        '800': '#7a7e78',
        '900': '#585c57',
        '950': '#373936',
      },
      accent: {
        '50': '#eef0e8',
        '100': '#ced4bb',
        '200': '#adb88e',
        '300': '#8c9b61',
        '400': '#74863f',
        '500': '#5c711e',
        '600': '#4e6019',
        '700': '#404f15',
        '800': '#323e10',
        '900': '#242d0c',
        '950': '#171c07',
      },
      quaternary: {
        '50': '#fbfdfa',
        '100': '#f3fbf0',
        '200': '#ebf9e6',
        '300': '#e3f7dc',
        '400': '#ddf5d4',
        '500': '#d8f4cd',
        '600': '#b7cfae',
        '700': '#97aa8f',
        '800': '#768670',
        '900': '#566152',
        '950': '#363d33',
      },
      quinary: {
        '50': '#e5f7e7',
        '100': '#b2e9b7',
        '200': '#7fdb88',
        '300': '#4ccd59',
        '400': '#26c235',
        '500': '#00b812',
        '600': '#009c0f',
        '700': '#00800c',
        '800': '#006509',
        '900': '#004907',
        '950': '#002e04',
      },
    }
  },
  // College Baseball Machine local
  'cbs.localhost:5184': {
    name: "The College Baseball Machine",
    faviconUrl: "/favicons/the-college-baseball-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-college-baseball-machine/apple-touch-icon.png",
    description: "Complete college baseball championship history, College World Series, and statistics",
    keywords: "college baseball championships, NCAA College World Series, baseball analytics",
    sport: 'baseball',
    level: 'college',
    logoUrl: "/logos/the-college-baseball-machine.svg",
    theme: {
      primary: {
        '50': '#e8e9e7',
        '100': '#babdba',
        '200': '#8d918c',
        '300': '#60655e',
        '400': '#3e443b',
        '500': '#1c2419',
        '600': '#171e15',
        '700': '#131911',
        '800': '#0f130d',
        '900': '#0b0e0a',
        '950': '#070906',
      },
      secondary: {
        '50': '#fbfcfb',
        '100': '#f5f7f4',
        '200': '#eef2ed',
        '300': '#e7ede5',
        '400': '#e2e9e0',
        '500': '#dee6db',
        '600': '#bcc3ba',
        '700': '#9ba199',
        '800': '#7a7e78',
        '900': '#585c57',
        '950': '#373936',
      },
      accent: {
        '50': '#eef0e8',
        '100': '#ced4bb',
        '200': '#adb88e',
        '300': '#8c9b61',
        '400': '#74863f',
        '500': '#5c711e',
        '600': '#4e6019',
        '700': '#404f15',
        '800': '#323e10',
        '900': '#242d0c',
        '950': '#171c07',
      },
      quaternary: {
        '50': '#fbfdfa',
        '100': '#f3fbf0',
        '200': '#ebf9e6',
        '300': '#e3f7dc',
        '400': '#ddf5d4',
        '500': '#d8f4cd',
        '600': '#b7cfae',
        '700': '#97aa8f',
        '800': '#768670',
        '900': '#566152',
        '950': '#363d33',
      },
      quinary: {
        '50': '#e5f7e7',
        '100': '#b2e9b7',
        '200': '#7fdb88',
        '300': '#4ccd59',
        '400': '#26c235',
        '500': '#00b812',
        '600': '#009c0f',
        '700': '#00800c',
        '800': '#006509',
        '900': '#004907',
        '950': '#002e04',
      },
    }
  },
  // College Hockey Machine production
  'the-college-hockey-machine.com': {
    name: "The College Hockey Machine",
    faviconUrl: "/favicons/the-college-hockey-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-college-hockey-machine/apple-touch-icon.png",
    description: "Complete college hockey championship history, NCAA Frozen Four, and statistics",
    keywords: "college hockey championships, NCAA Frozen Four, hockey analytics",
    sport: 'hockey',
    level: 'college',
    logoUrl: "/logos/the-college-hockey-machine.svg",
    theme: {
      primary: {
        '50': '#e8e8e8',
        '100': '#bcbbbc',
        '200': '#908f8f',
        '300': '#636262',
        '400': '#424041',
        '500': '#211f20',
        '600': '#1c1a1b',
        '700': '#171516',
        '800': '#121111',
        '900': '#0d0c0c',
        '950': '#080708',
      },
      secondary: {
        '50': '#ffffff',
        '100': '#fafafa',
        '200': '#f5f5f5',
        '300': '#e0e0e0',
        '400': '#bdbdbd',
        '500': '#9e9e9e',
        '600': '#757575',
        '700': '#616161',
        '800': '#424242',
        '900': '#212121',
        '950': '#0a0a0a',
      },
      accent: {
        '50': '#f1f1ef',
        '100': '#d6d5cf',
        '200': '#bbb9b0',
        '300': '#a09d91',
        '400': '#8c8879',
        '500': '#787462',
        '600': '#666253',
        '700': '#545144',
        '800': '#423f35',
        '900': '#302e27',
        '950': '#1e1d18',
      },
      quaternary: {
        '50': '#f7f6f3',
        '100': '#e7e5dd',
        '200': '#d7d5c6',
        '300': '#c7c4af',
        '400': '#bbb79e',
        '500': '#afab8e',
        '600': '#949178',
        '700': '#7a7763',
        '800': '#605e4e',
        '900': '#464438',
        '950': '#2b2a23',
      },
      quinary: {
        '50': '#eef4fa',
        '100': '#cde0f1',
        '200': '#accce8',
        '300': '#8bb7de',
        '400': '#72a8d7',
        '500': '#5a99d1',
        '600': '#4c82b1',
        '700': '#3e6b92',
        '800': '#315472',
        '900': '#243d53',
        '950': '#162634',
      },
    }
  },
  'www.the-college-hockey-machine.com': {
    name: "The College Hockey Machine",
    description: "Complete college hockey championship history, NCAA Frozen Four, and statistics",
    keywords: "college hockey championships, NCAA Frozen Four, hockey analytics",
    sport: 'hockey',
    level: 'college',
    logoUrl: "/logos/the-college-hockey-machine.svg",
    faviconUrl: "/favicons/the-college-hockey-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-college-hockey-machine/apple-touch-icon.png",
    theme: {
      primary: {
        '50': '#e8e8e8',
        '100': '#bcbbbc',
        '200': '#908f8f',
        '300': '#636262',
        '400': '#424041',
        '500': '#211f20',
        '600': '#1c1a1b',
        '700': '#171516',
        '800': '#121111',
        '900': '#0d0c0c',
        '950': '#080708',
      },
      secondary: {
        '50': '#ffffff',
        '100': '#fafafa',
        '200': '#f5f5f5',
        '300': '#e0e0e0',
        '400': '#bdbdbd',
        '500': '#9e9e9e',
        '600': '#757575',
        '700': '#616161',
        '800': '#424242',
        '900': '#212121',
        '950': '#0a0a0a',
      },
      accent: {
        '50': '#f1f1ef',
        '100': '#d6d5cf',
        '200': '#bbb9b0',
        '300': '#a09d91',
        '400': '#8c8879',
        '500': '#787462',
        '600': '#666253',
        '700': '#545144',
        '800': '#423f35',
        '900': '#302e27',
        '950': '#1e1d18',
      },
      quaternary: {
        '50': '#f7f6f3',
        '100': '#e7e5dd',
        '200': '#d7d5c6',
        '300': '#c7c4af',
        '400': '#bbb79e',
        '500': '#afab8e',
        '600': '#949178',
        '700': '#7a7763',
        '800': '#605e4e',
        '900': '#464438',
        '950': '#2b2a23',
      },
      quinary: {
        '50': '#eef4fa',
        '100': '#cde0f1',
        '200': '#accce8',
        '300': '#8bb7de',
        '400': '#72a8d7',
        '500': '#5a99d1',
        '600': '#4c82b1',
        '700': '#3e6b92',
        '800': '#315472',
        '900': '#243d53',
        '950': '#162634',
      },
    }
  },
  // College Hockey Machine local
  'chk.localhost:5184': {
    name: "The College Hockey Machine",
    faviconUrl: "/favicons/the-college-hockey-machine/favicon.ico",
    appleTouchIconUrl: "/favicons/the-college-hockey-machine/apple-touch-icon.png",
    description: "Complete college hockey championship history, NCAA Frozen Four, and statistics",
    keywords: "college hockey championships, NCAA Frozen Four, hockey analytics",
    sport: 'hockey',
    level: 'college',
    logoUrl: "/logos/the-college-hockey-machine.svg",
    theme: {
      primary: {
        '50': '#e8e8e8',
        '100': '#bcbbbc',
        '200': '#908f8f',
        '300': '#636262',
        '400': '#424041',
        '500': '#211f20',
        '600': '#1c1a1b',
        '700': '#171516',
        '800': '#121111',
        '900': '#0d0c0c',
        '950': '#080708',
      },
      secondary: {
        '50': '#ffffff',
        '100': '#fafafa',
        '200': '#f5f5f5',
        '300': '#e0e0e0',
        '400': '#bdbdbd',
        '500': '#9e9e9e',
        '600': '#757575',
        '700': '#616161',
        '800': '#424242',
        '900': '#212121',
        '950': '#0a0a0a',
      },
      accent: {
        '50': '#f1f1ef',
        '100': '#d6d5cf',
        '200': '#bbb9b0',
        '300': '#a09d91',
        '400': '#8c8879',
        '500': '#787462',
        '600': '#666253',
        '700': '#545144',
        '800': '#423f35',
        '900': '#302e27',
        '950': '#1e1d18',
      },
      quaternary: {
        '50': '#f7f6f3',
        '100': '#e7e5dd',
        '200': '#d7d5c6',
        '300': '#c7c4af',
        '400': '#bbb79e',
        '500': '#afab8e',
        '600': '#949178',
        '700': '#7a7763',
        '800': '#605e4e',
        '900': '#464438',
        '950': '#2b2a23',
      },
      quinary: {
        '50': '#eef4fa',
        '100': '#cde0f1',
        '200': '#accce8',
        '300': '#8bb7de',
        '400': '#72a8d7',
        '500': '#5a99d1',
        '600': '#4c82b1',
        '700': '#3e6b92',
        '800': '#315472',
        '900': '#243d53',
        '950': '#162634',
      },
    }
  }
};

export const useSiteConfig = () => {
  const hostname = window.location.hostname + (window.location.port ? ':' + window.location.port : '');
  console.log('🌐 Current hostname:', hostname);
  console.log('📋 Available configs:', Object.keys(SITE_CONFIGS));
  const config = SITE_CONFIGS[hostname] || SITE_CONFIGS['the-sports-machine.com'];
  console.log('✅ Selected config:', config.name, 'Sport:', config.sport || 'all');
  
  useEffect(() => {
    // Apply site-specific theme using the utility function
    applyThemeToDocument(config.theme);
    console.log('🎨 Applied theme for:', config.name);
    console.log('🎨 Primary color:', config.theme.primary[500]);
    
    // Also update body class for debugging
    document.body.setAttribute('data-site', config.name.toLowerCase().replace(/ /g, '-'));
  }, [config]);
  
  return config;
};